import * as Dialog from "@radix-ui/react-dialog";
import { type FormEvent, useEffect, useState } from "react";

import { Button } from "./button";
import { Field } from "./field";
import { Input } from "./input";

type PromptDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  label: string;
  placeholder?: string;
  initialValue?: string;
  submitLabel: string;
  /** Return an error message to keep the dialog open, or null to close it. */
  onSubmit: (value: string) => string | null;
  /** Optional secondary action (e.g. "Remove link"). */
  secondaryAction?: { label: string; onClick: () => void };
};

/** A one-field dialog — used for links, embeds and image alt text. */
export const PromptDialog = ({
  open,
  onOpenChange,
  title,
  label,
  placeholder,
  initialValue = "",
  submitLabel,
  onSubmit,
  secondaryAction,
}: PromptDialogProps) => {
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setValue(initialValue);
      setError(null);
    }
  }, [open, initialValue]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const problem = onSubmit(value.trim());
    if (problem) setError(problem);
    else onOpenChange(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/30 backdrop-blur-[1px]" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(28rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-line bg-white p-6 shadow-xl">
          <Dialog.Title className="font-serif text-lg text-ink">{title}</Dialog.Title>
          <Dialog.Description className="sr-only">{label}</Dialog.Description>
          <form onSubmit={submit} className="mt-4 space-y-5">
            <Field label={label} error={error}>
              {(fieldProps) => (
                <Input
                  {...fieldProps}
                  autoFocus
                  value={value}
                  placeholder={placeholder}
                  onChange={(event) => setValue(event.target.value)}
                />
              )}
            </Field>
            <div className="flex items-center justify-between gap-2">
              <div>
                {secondaryAction && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      secondaryAction.onClick();
                      onOpenChange(false);
                    }}
                  >
                    {secondaryAction.label}
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Dialog.Close asChild>
                  <Button type="button" variant="secondary">
                    Cancel
                  </Button>
                </Dialog.Close>
                <Button type="submit">{submitLabel}</Button>
              </div>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
